import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Image, Upload, UploadProps } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FileConfig } from '../../../../../constants/config';
import { RootState } from '../../../../../store';
import { TIME_FORMAT } from '../../../../../util/constants';
import { Helper } from '../../../../../util/helper';
import { RemoveImageIcon } from '../../../chatSupport/@svg/remove';
import { UploadFileSvg } from '../../../chatSupport/@svg/upload';
import { SendMessageIcon, UnSendMessageIcon } from '../../@svg/send';
import { FEE_STATUS, FEE_STATUS_LABEL, ThreadTimeline } from './inquiryFeeTypes';
import { CHANGEABLE_INQUIRY_OPTIONS, InquiryOption } from './inquiryOptions';

export interface ComposerFile {
  preview: string;
  source: File;
}

interface CardComposerProps {
  onSend: (text: string, files: ComposerFile[], price?: number) => void;
  placeholder?: string;
  // ẩn ô nhập giá ở thread お問い合わせ chung (không báo giá)
  showPrice?: boolean;
}

export const CardComposer = ({ onSend, placeholder, showPrice = true }: CardComposerProps) => {
  const [text, setText] = useState('');
  const [price, setPrice] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [files, setFiles] = useState<ComposerFile[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const canSend = !!text.trim() || files.length > 0 || !!price.trim();

  // Giữ input cao 34px khi 1 dòng, tăng dần theo nội dung khi xuống dòng (giới hạn theo max-height CSS).
  const autoResize = (el: HTMLTextAreaElement) => {
    el.style.height = '34px';
    if (el.scrollHeight > el.clientHeight) {
      // box-sizing: border-box nên height phải gồm cả border, trong khi scrollHeight chỉ tính content + padding.
      // Thiếu 2px đó thì từ dòng thứ 2 trở đi dòng cuối luôn bị cắt hụt (đã đo trên trình duyệt: hidden = 2px).
      const borderHeight = el.offsetHeight - el.clientHeight;
      el.style.height = `${el.scrollHeight + borderHeight}px`;
    }
  };

  const handleChangeText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    autoResize(e.target);
  };

  const handleChangePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // chỉ cho số + tối đa 1 dấu chấm thập phân
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleChangeUpload: UploadProps['onChange'] = ({ fileList: newList }) => {
    if (!!newList?.length) {
      setFileList(newList);
      const urls = newList.map((file: any) => ({
        preview: URL.createObjectURL(file?.originFileObj),
        source: file?.originFileObj,
      }));
      setFiles(urls);
    }
  };

  const handleRemove = (file: ComposerFile) => {
    setFiles((prev) => prev.filter((item) => item.preview !== file.preview));
    setFileList((prev) => prev.filter((item) => item?.originFileObj !== file.source));
  };

  const handleSend = () => {
    if (!canSend) return;
    const priceValue = price.trim() ? Number(price) : undefined;
    onSend(text.trim(), files, priceValue);
    setText('');
    setPrice('');
    setFiles([]);
    setFileList([]);
    if (inputRef.current) inputRef.current.style.height = '34px';
  };

  return (
    <div className="thread-card-composer-wrap">
      {files.length > 0 && (
        <div className="thread-card-composer-uploads">
          {files.map((file) => (
            <div key={file.preview} className="thread-card-composer-uploads-item">
              <Image preview={false} width={40} height={40} className="rounded" src={file.preview} />
              <span className="thread-card-composer-uploads-close" onClick={() => handleRemove(file)}>
                <RemoveImageIcon />
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="thread-card-composer">
        {showPrice && (
          <input
            className="thread-card-composer-price"
            inputMode="decimal"
            placeholder="金額 $（任意）"
            value={price}
            onChange={handleChangePrice}
          />
        )}
        <textarea
          ref={inputRef}
          className="thread-card-composer-input"
          rows={1}
          placeholder={placeholder || 'メッセージを入力'}
          value={text}
          onChange={handleChangeText}
        />
        <Upload
          className="thread-card-composer-img"
          multiple={true}
          maxCount={5}
          showUploadList={false}
          onChange={handleChangeUpload}
          fileList={fileList}
          accept={FileConfig.imageAccept}
        >
          <UploadFileSvg />
        </Upload>
        <span className={`thread-card-composer-send ${canSend ? '' : 'disabled'}`} onClick={handleSend}>
          {canSend ? <SendMessageIcon /> : <UnSendMessageIcon />}
        </span>
      </div>
    </div>
  );
};

const MessageBubble = ({ data, onDemote }: { data: any; onDemote?: (message: any) => void }) => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const images = data?.itemMessageImages || [];
  const time = dayjs(data?.createdAt).format(TIME_FORMAT.MESSAGE_TIME);
  const isStaff = +data?.sendSide !== 0;
  const isMine =
    isStaff &&
    ((authUser?.id != null && +data?.sendStaffId === +authUser?.id) ||
      !!data?.sendStaff?.role?.includes(authUser?.role));

  const imageBlock = (alignEnd: boolean) =>
    !!images.length && (
      <div className={`d-flex flex-column ${alignEnd ? 'align-items-end' : ''}`}>
        <span className="item-detail_message-file">
          <Image.PreviewGroup>
            {images.map((img: any, i: number) => (
              <Image key={i} width={98} height={98} className="rounded" src={Helper.getSourceFile(img?.source)} />
            ))}
          </Image.PreviewGroup>
        </span>
      </div>
    );

  // Tin của user (bên trái). Chỉ tin của user mới demote được (khớp prototype chat.html).
  if (!isStaff) {
    const demoteBtn = onDemote ? (
      <button
        type="button"
        className="thread-msg-move"
        title="一般のお問い合わせに変更"
        aria-label="一般のお問い合わせに変更"
        onClick={() => onDemote(data)}
      >
        <MessageOutlined />
      </button>
    ) : null;
    return (
      <div className="item-detail_message_content_other">
        <div className="item-detail_message_content_other_avatar">
          <Avatar size={32} src={Helper.getSourceFile(data?.sendUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_other_text w-100">
          {imageBlock(false)}
          {!!data?.message ? (
            <div className="thread-msg-moverow">
              <p className="item-detail_message_content_other_text_content m-t-2">{data.message}</p>
              {demoteBtn}
            </div>
          ) : (
            // tin chỉ có ảnh: vẫn cho demote — icon đứng riêng dưới khối ảnh
            demoteBtn && <div className="thread-msg-moverow">{demoteBtn}</div>
          )}
          <p className="item-detail_message_content_other_text_name m-t-2">
            {data?.sendUser?.name || data?.sendUser?.emailAddress}
          </p>
          <p className="item-detail_message_content_other_text_time">{time}</p>
        </div>
      </div>
    );
  }

  // Tin của staff cùng role đang đăng nhập (bên phải, dùng avatar authUser).
  if (isMine) {
    return (
      <div className="item-detail_message_content_me convert-me-send">
        <div className="item-detail_message_content_me_avatar me-send">
          <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_me_text w-100">
          {imageBlock(true)}
          {!!data?.message && (
            <div className="d-flex justify-content-end">
              <p className="item-detail_message_content_me_text_content m-t-2">{data.message}</p>
            </div>
          )}
          <p className="item-detail_message_content_me_text_name m-t-2 me-send">
            {data?.sendStaff?.name || data?.sendStaff?.loginId || authUser?.name || authUser?.loginId}
          </p>
          <p className="item-detail_message_content_me_text_time me-send">{time}</p>
        </div>
      </div>
    );
  }

  // Tin của staff khác role.
  return (
    <div className="item-detail_message_content_me">
      <div className="item-detail_message_content_me_avatar">
        <Avatar size={32} src={Helper.getSourceFile(data?.sendStaff?.avatar?.source)} />
      </div>
      <div className="item-detail_message_content_me_text w-100">
        {imageBlock(false)}
        {!!data?.message && <p className="item-detail_message_content_me_text_content-other m-t-2">{data.message}</p>}
        <p className="item-detail_message_content_me_text_name m-t-2">
          {data?.sendStaff?.name || data?.sendStaff?.loginId}
        </p>
        <p className="item-detail_message_content_me_text_time">{time}</p>
      </div>
    </div>
  );
};

const FeeCard = ({ fee, staffById }: { fee: any; staffById?: Record<number, any> }) => {
  const { authUser } = useSelector((state: RootState) => state.auth);
  const status = fee?.status;
  const invalid = status === FEE_STATUS.SUPERSEDED || status === FEE_STATUS.CANCELED || status === FEE_STATUS.REFUNDED;
  const time = fee?.createdAt ? dayjs(fee.createdAt).format(TIME_FORMAT.MESSAGE_TIME) : '';
  const proposedById = fee?.proposedByStaffId ?? fee?.proposedByStaff?.id;
  const proposer = fee?.proposedByStaff ?? (proposedById != null ? staffById?.[proposedById] : undefined);
  const isMine =
    (authUser?.id != null && proposedById != null && +proposedById === +authUser?.id) ||
    !!proposer?.role?.includes(authUser?.role);

  const quote = (
    <div className={`inquiry-fee-quote ${invalid ? 'is-invalid' : ''}`}>
      <div className="inquiry-fee-quote-row">
        <span className="inquiry-fee-quote-title">費用</span>
        <span className="inquiry-fee-quote-amount">${Number(fee?.chargeAmount ?? 0).toFixed(2)}</span>
      </div>
      {status === FEE_STATUS.PROPOSED ? (
        <div className="inquiry-fee-quote-status waiting">
          <ClockCircleOutlined />
          <span>同意・支払い待ち</span>
        </div>
      ) : (
        <div className="inquiry-fee-quote-status">{FEE_STATUS_LABEL[status] || status}</div>
      )}
    </div>
  );

  if (isMine) {
    return (
      <div className="item-detail_message_content_me convert-me-send">
        <div className="item-detail_message_content_me_avatar me-send">
          <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)} />
        </div>
        <div className="item-detail_message_content_me_text w-100">
          {!!fee?.note && (
            <div className="d-flex justify-content-end">
              <p className="item-detail_message_content_me_text_content m-t-2">{fee.note}</p>
            </div>
          )}
          <div className="d-flex justify-content-end">{quote}</div>
          <p className="item-detail_message_content_me_text_name m-t-2 me-send">
            {proposer?.name || proposer?.loginId || authUser?.name || authUser?.loginId}
          </p>
          <p className="item-detail_message_content_me_text_time me-send">{time}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="item-detail_message_content_other">
      <div className="item-detail_message_content_other_avatar">
        <Avatar size={32} src={Helper.getSourceFile(proposer?.avatar?.source)} />
      </div>
      <div className="item-detail_message_content_other_text w-100">
        {!!fee?.note && <p className="item-detail_message_content_other_text_content m-t-2">{fee.note}</p>}
        {quote}
        <p className="item-detail_message_content_other_text_name m-t-2">{proposer?.name || proposer?.loginId}</p>
        <p className="item-detail_message_content_other_text_time">{time}</p>
      </div>
    </div>
  );
};

interface InquiryThreadCardProps {
  thread: ThreadTimeline;
  option?: InquiryOption;
  onSend: (text: string, files: ComposerFile[], price?: number) => void;
  onComplete?: (feeId: number) => void;
  // Admin đổi loại option của thread. Không truyền = không cho đổi (vd thread お問い合わせ chung).
  onChangeType?: (optionType: string) => void;
  // Admin demote 1 tin của user về thread お問い合わせ chung. Không truyền = ẩn icon.
  onDemote?: (message: any) => void;
  // Admin huỷ cả thread option (依頼をキャンセル). Không truyền = ẩn nút.
  onCancel?: () => void;
  placeholder?: string;
  staffById?: Record<number, any>;
}

export const InquiryThreadCard = ({
  thread,
  option,
  onSend,
  onComplete,
  onChangeType,
  onDemote,
  onCancel,
  placeholder,
  staffById,
}: InquiryThreadCardProps) => {
  // Trạng thái báo giá của thread (dựa trên các fee trong timeline).
  const fees = (thread.timeline || []).filter((i) => i.kind === 'fee').map((i) => i.data);
  const paidFee = fees.find((f) => f?.status === FEE_STATUS.PAID); // đã trả, chưa hoàn thành → cho phép complete
  const completedFee = fees.find((f) => f?.status === FEE_STATUS.COMPLETED);
  // Thread đã bị huỷ (依頼をキャンセル): coi như đóng — không báo giá, không đổi loại, không hoàn thành.
  // Vẫn chat được nên composer giữ nguyên.
  const canceled = !!thread.canceledAt;
  // Sau khi đã thanh toán/hoàn thành/huỷ thì không báo giá lại nữa → ẩn ô nhập giá.
  const hasPaidOrCompleted = !!paidFee || !!completedFee || canceled;
  // Đổi loại: chỉ thread option và chưa hoàn thành (thread đã 完了 coi như đóng — khớp prototype).
  // Đã có báo giá VẪN đổi được: BE gộp thread cùng loại và supersede các báo giá cũ.
  const canChangeType = !!option && !!onChangeType && !completedFee && !canceled;
  // Huỷ: thread option chưa hoàn thành và chưa huỷ. Đã thanh toán thì BE hoàn 100% tiền.
  // Admin huỷ được cả thread đã 完了 (rule KH 2026-10-05「いつでもキャンセル可」); BE hoàn trừ キャンセル料.
  const canCancel = !!option && !!onCancel && !canceled;

  // Dòng hệ thống (đã thanh toán / đã hoàn thành) là SỰ KIỆN có thời điểm riêng (paidAt/completedAt),
  // không phải phần phụ của card báo giá. Đưa vào timeline rồi sort theo thời gian để tin nhắn gửi
  // SAU khi hoàn thành nằm BÊN DƯỚI dòng thông báo (trước đây thông báo là footer nên luôn ở cuối).
  type RenderItem = { at: number; key: string; node: React.ReactNode };
  const renderItems: RenderItem[] = [];

  (thread.timeline || []).forEach((item, idx) => {
    const at = dayjs(item.at).valueOf();
    if (item.kind === 'fee') {
      const fee = item.data;
      renderItems.push({ at, key: `fee-${fee?.id}-${idx}`, node: <FeeCard fee={fee} staffById={staffById} /> });

      const isPaid = fee?.status === FEE_STATUS.PAID || fee?.status === FEE_STATUS.COMPLETED || !!fee?.paidAt;
      const isDone = fee?.status === FEE_STATUS.COMPLETED || !!fee?.completedAt;
      // Thiếu paidAt/completedAt (payload cũ) → neo vào thời điểm báo giá như trước.
      if (isPaid) {
        renderItems.push({
          at: fee?.paidAt ? dayjs(fee.paidAt).valueOf() : at,
          key: `paid-${fee?.id}-${idx}`,
          node: (
            <div className="thread-card-system">
              <CheckCircleOutlined />
              <span>お支払いが完了しました（${Number(fee?.chargeAmount ?? 0).toFixed(2)}）</span>
            </div>
          ),
        });
      }
      if (isDone) {
        renderItems.push({
          at: fee?.completedAt ? dayjs(fee.completedAt).valueOf() : at,
          key: `done-${fee?.id}-${idx}`,
          node: (
            <div className="thread-card-system">
              <CheckCircleOutlined />
              <span>この依頼の対応が完了しました</span>
            </div>
          ),
        });
      }
      // Admin huỷ thread sau khi đã thu → đã hoàn tiền: mốc riêng tại refundedAt, nằm SAU dòng đã thanh toán.
      if (fee?.status === FEE_STATUS.REFUNDED || !!fee?.refundedAt) {
        renderItems.push({
          at: fee?.refundedAt ? dayjs(fee.refundedAt).valueOf() : at,
          key: `refund-${fee?.id}-${idx}`,
          node: (
            <div className="thread-card-system thread-card-system--refund">
              {/* Hoàn sau khi trừ キャンセル料 (cancelFee; null = dữ liệu cũ hoàn 100%) */}
              <span>返金しました（${(Number(fee?.chargeAmount ?? 0) - Number(fee?.cancelFee ?? 0)).toFixed(2)}）</span>
            </div>
          ),
        });
      }
      return;
    }
    // Tin hệ thống BE tự tạo (systemType, vd PAYMENT) chỉ để 依頼管理 đẩy item + chấm chưa đọc —
    // không render: card báo giá đã có dòng お支払いが完了しました rồi.
    if (item.data?.systemType) return;
    renderItems.push({
      at,
      key: `msg-${item.data?.id}-${idx}`,
      node: <MessageBubble data={item.data} onDemote={onDemote} />,
    });
  });

  // Array.sort ổn định ⇒ dòng hệ thống trùng mốc thời gian vẫn nằm ngay sau card báo giá của nó.
  // Thread đã huỷ → mốc riêng tại canceledAt, cùng kiểu với dòng 「問い合わせが終了しました」 của card chung
  // (yêu cầu khách 2026-10-02). Nằm sau dòng 返金しました vì canceledAt được ghi ở vòng cuối của cancelThread.
  if (thread.canceledAt) {
    renderItems.push({
      at: dayjs(thread.canceledAt).valueOf(),
      key: `canceled-${thread.itemMessageGroupId}`,
      node: (
        <div className="item-detail_message_content_seen">
          <p>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M4.125 13.125L9.375 18.375L19.875 7.125"
                stroke="#514B4D"
                strokeWidth="1.875"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>この依頼はキャンセルされました</span>
          </p>
        </div>
      ),
    });
  }

  renderItems.sort((a, b) => a.at - b.at);

  return (
    <div className="thread-card">
      {option && (
        <div className="thread-card-head">
          {canChangeType ? (
            <Dropdown
              trigger={['click']}
              placement="bottomLeft"
              overlayClassName="thread-card-typemenu"
              menu={{
                selectable: false,
                items: [
                  { key: '__title', type: 'group', label: 'オプション種別を変更' },
                  ...CHANGEABLE_INQUIRY_OPTIONS.map((opt) => ({
                    key: opt.value,
                    label: (
                      <span className="thread-card-typemenu-item">
                        <span className="thread-card-typemenu-item-icon">{opt.icon}</span>
                        <span>{opt.label}</span>
                        {opt.value === option.value && <CheckOutlined className="thread-card-typemenu-item-check" />}
                      </span>
                    ),
                  })),
                ],
                onClick: ({ key }) => {
                  if (key !== option.value) onChangeType?.(key);
                },
              }}
            >
              <button type="button" className="thread-card-head-type">
                <span className="thread-card-head-icon">{option.icon}</span>
                <span>{option.label}</span>
                <DownOutlined className="thread-card-head-type-caret" />
              </button>
            </Dropdown>
          ) : (
            <>
              <span className="thread-card-head-icon">{option.icon}</span>
              <span>{option.label}</span>
            </>
          )}
          {/* Trạng thái hoàn thành chuyển lên header: trong timeline nó là 1 mốc thời gian,
              không còn là footer ghim đáy card. */}
          {completedFee && (
            <span className="thread-card-head-done">
              <CheckCircleOutlined />
              完了
            </span>
          )}
          {canceled && <span className="thread-card-head-canceled">キャンセル済み</span>}
          {canCancel && (
            <button type="button" className="thread-card-head-cancel" onClick={() => onCancel?.()}>
              キャンセル
            </button>
          )}
        </div>
      )}
      <div className="thread-card-body">
        {renderItems.map((item) => (
          <Fragment key={item.key}>{item.node}</Fragment>
        ))}
      </div>

      {/* Đã thanh toán (chưa hoàn thành) → nút 対応完了にする. */}
      {option && !completedFee && !canceled && paidFee ? (
        <button type="button" className="thread-card-donebtn" onClick={() => onComplete?.(paidFee.id)}>
          <CheckCircleOutlined />
          対応完了にする
        </button>
      ) : null}

      <CardComposer onSend={onSend} placeholder={placeholder} showPrice={!!option && !hasPaidOrCompleted} />
    </div>
  );
};
